import { useEffect, useRef, useState } from 'react';

import type { Dispatch, SetStateAction } from 'react';

export interface UseControlledFromServerOptions<T> {
  /**
   * When true, the hook does NOT resync localValue from serverValue. This
   * prevents a server refetch mid-edit from stomping the user's in-progress edit.
   */
  isEditing?: boolean;
  /** Custom equality check used to decide whether serverValue actually changed. */
  isEqual?: (a: T, b: T) => boolean;
}

/**
 * Mirrors a server-owned value into local state, resyncing only when the user is
 * not actively editing.
 *
 * Returns a tuple of [localValue, setLocalValue, resyncNow]:
 * - localValue: the current local value (initialized from serverValue)
 * - setLocalValue: updates local value during an edit
 * - resyncNow: forces local value back to the latest serverValue
 *
 * The resync effect lists serverValue in its dependency array and gates the
 * state update on isEditing. The exhaustive-deps suppression lives here so the
 * lint suppression is isolated to one place rather than every consumer.
 */
export function useControlledFromServer<T>(
  serverValue: T,
  options?: UseControlledFromServerOptions<T>
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const { isEditing, isEqual } = options ?? {};
  const [localValue, setLocalValue] = useState<T>(serverValue);

  // Keep the latest equality check without retriggering the resync effect.
  const isEqualRef = useRef(isEqual);
  useEffect(() => {
    isEqualRef.current = isEqual;
  }, [isEqual]);

  useEffect(() => {
    if (isEditing) return;

    const areEqual = isEqualRef.current
      ? isEqualRef.current(localValue, serverValue)
      : Object.is(localValue, serverValue);

    if (areEqual) return;

    setLocalValue(serverValue);
    // localValue is read but intentionally omitted: this effect resyncs when the
    // server value changes, gated on isEditing, and must not loop on its own writes.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [serverValue, isEditing]);

  function resyncNow() {
    setLocalValue(serverValue);
  }

  return [localValue, setLocalValue, resyncNow];
}
